/**
 * Festival Program Hub Selector
 * Handles dynamic filtering of events by festival hub using AJAX
 */

class FestivalProgram {
    constructor(panelRoot) {
        this.root = panelRoot;
        this.hubButtons = this.root.querySelectorAll('.hub-selector-btn');
        this.eventsContainer = this.root.querySelector('#events-container');
        this.loadingState = this.root.querySelector('#loading-state');
        this.noEventsState = this.root.querySelector('#no-events-state');
        this.currentHubId = null;
        
        // Request control to prevent race conditions when switching hubs quickly
        this.currentRequestController = null;
        this.requestCounter = 0;
        this.activeRequestId = 0;
        
        this.init();
    }
    
    init() {
        if (!this.hubButtons.length || !this.eventsContainer) {
            return;
        }
        
        // Set initial hub from active button within this panel
        const activeButton = this.root.querySelector('.hub-selector-btn[data-active="true"]');
        if (activeButton) {
            this.currentHubId = parseInt(activeButton.dataset.hubId);
        }
        
        // Bind click events
        this.hubButtons.forEach(button => {
            button.addEventListener('click', (e) => this.handleHubClick(e));
        });
        
        // Check URL for fhub query parameter and activate matching hub on load
        this.applyHubFromQuery();
    }
    
    normalizeHub(value) {
        return (value || '')
            .toString()
            .trim()
            .toLowerCase()
            .replace(/\s+/g, '-')
            .replace(/[^a-z0-9-]/g, '')
            .replace(/-+/g, '-');
    }
    
    applyHubFromQuery() {
        try {
            const params = new URLSearchParams(window.location.search || '');
            const fhubRaw = params.get('fhub');
            if (!fhubRaw) { return; }
            const target = this.normalizeHub(fhubRaw);
            if (!target) { return; }

            // Find a matching hub button by normalized data-hub-title
            let matchedButton = null;
            this.hubButtons.forEach(btn => {
                const title = btn.getAttribute('data-hub-title') || '';
                if (this.normalizeHub(title) === target) {
                    matchedButton = btn;
                }
            });

            if (matchedButton) {
                const hubId = parseInt(matchedButton.dataset.hubId);
                const hubTitle = matchedButton.dataset.hubTitle;
                if (!isNaN(hubId) && hubId !== this.currentHubId) {
                    this.updateActiveButton(matchedButton);
                    this.loadEvents(hubId, hubTitle);
                }
            }
        } catch (e) {
            // Fail gracefully if URL parsing fails
        }
    }
    
    handleHubClick(event) {
        const button = event.target;
        const hubId = parseInt(button.dataset.hubId);
        const hubTitle = button.dataset.hubTitle;
        
        // Don't reload if same hub is selected
        if (hubId === this.currentHubId) {
            return;
        }
        
        this.updateActiveButton(button);
        this.loadEvents(hubId, hubTitle);
    }
    
    updateActiveButton(activeButton) {
        // Remove active state from all buttons
        this.hubButtons.forEach(btn => {
            btn.classList.remove('text-purple-core');
            btn.classList.add('text-black-core');
            btn.removeAttribute('data-active');
        });
        
        // Set active state on clicked button
        activeButton.classList.remove('text-black-core');
        activeButton.classList.add('text-purple-core');
        activeButton.setAttribute('data-active', 'true');
        
        this.currentHubId = parseInt(activeButton.dataset.hubId);

        // Close the accordion on hub change
        try {
            const filterAccordions = this.root.querySelectorAll('.festival-program-grid [data-tw-accordion][data-tw-toggle-container]');
            for (const filterAccordion of filterAccordions) {
                filterAccordion.setAttribute('data-open', 'false');

                const removeThis = filterAccordion.querySelector('.group-data-\\[open\\=true\\]\\/accordion\\:overflow-visible');
                removeThis?.classList.remove('group-data-[open=true]/accordion:overflow-visible');
            }
        } catch(e) {}
    }
    
    async loadEvents(hubId, hubTitle) {
        // Abort previous in-flight request if any
        try {
            if (this.currentRequestController) {
                this.currentRequestController.abort();
            }
        } catch (e) { /* noop */ }

        // Create controller and request id for this fetch
        const controller = new AbortController();
        this.currentRequestController = controller;
        const requestId = ++this.requestCounter;
        this.activeRequestId = requestId;

        try {
            // Show loading state
            this.showLoadingState();

            // Make AJAX request to get filtered events
            const formData = new URLSearchParams({
                action: 'barking-gecko-arts/festival-program/get-hub-events',
                hubId: hubId
            });

            // Add CSRF token if available
            if (window.csrfTokenName && window.csrfTokenValue) {
                formData.append(window.csrfTokenName, window.csrfTokenValue);
            }

            const response = await fetch('/', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded',
                    'X-Requested-With': 'XMLHttpRequest'
                },
                body: formData,
                signal: controller.signal
            });


            if (!response.ok) {
                const errorText = await response.text();
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            let data;
            try {
                data = await response.json();
            } catch (jsonErr) {
                // If JSON parse fails, rethrow
                throw jsonErr;
            }

            // Ignore if a newer request has started since this one
            if (requestId !== this.activeRequestId) {
                return;
            }

            // Update events container with new content
            this.updateEventsContainer(data.html, data.eventCount, hubTitle);

        } catch (error) {
            if (error?.name === 'AbortError') {
                // Request was aborted because a newer hub was selected; ignore silently
                return;
            }
            this.showErrorState();
        } finally {
            // Clear controller if this was the last active request
            if (requestId === this.activeRequestId) {
                this.currentRequestController = null;
            }
        }
    }
    
    updateEventsContainer(html, eventCount, hubTitle) {
        this.hideAllStates();

        // Always show the events container with the returned HTML
        // The controller now handles both events and content panels
        this.eventsContainer.innerHTML = html;
        this.eventsContainer.style.display = 'grid';

        // Show/hide entire card filter module based on whether there are events
        // Target the specific card filter module by ID
        const cardFilterModule = this.root.querySelector('#card-filter-module');
        if (cardFilterModule) {
            if (eventCount > 0) {
                cardFilterModule.dataset.active = 'true';
            } else {
                cardFilterModule.dataset.active = 'false';
            }
        }

        // Reset and re-apply filters to new cards if filtering module is present (single instance)
        if (window.EventFilters) {
            try { typeof window.EventFilters.reset === 'function' && window.EventFilters.reset(); } catch (e) {}
            try { typeof window.EventFilters.refresh === 'function' && window.EventFilters.refresh(); } catch (e) {}
        }

        // Trigger fade-in animation
        this.eventsContainer.classList.add('animate-fade-in');
        setTimeout(() => {
            this.eventsContainer.classList.remove('animate-fade-in');

            // Immediately reset filters after collapsing, before loading new hub (single instance)
            try {
                if (window.EventFilters && typeof window.EventFilters.reset === 'function') {
                    window.EventFilters.reset();
                }
            } catch (e) {}
        }, 400);
    }
    
    showLoadingState() {
        this.hideAllStates();
        if (this.loadingState) {
            this.loadingState.style.display = 'flex';
        }
    }
    
    showNoEventsState(hubTitle) {
        this.hideAllStates();
        if (this.noEventsState) {
            const message = this.noEventsState.querySelector('p');
            if (message) {
                message.textContent = `No events found for ${hubTitle}.`;
            }
            this.noEventsState.style.display = 'flex';
        }
    }
    
    showErrorState() {
        this.hideAllStates();
        this.eventsContainer.innerHTML = `
            <div class="col-span-full flex justify-center items-center py-12">
                <div class="text-center">
                    <p class="text-red-primary text-lg">Error loading events. Please try again.</p>
                </div>
            </div>
        `;
        this.eventsContainer.style.display = 'grid';
    }
    
    hideAllStates() {
        this.eventsContainer.style.display = 'none';
        if (this.loadingState) {
            this.loadingState.style.display = 'none';
        }
        if (this.noEventsState) {
            this.noEventsState.style.display = 'none';
        }
    }
}

// Initialize once on window load for the first .festival-program-grid
window.addEventListener('load', () => {
    const grid = document.querySelector('.festival-program-grid');
    if (!grid) return; // no grid, do nothing
    const root = grid.closest('.awesome-grid') || document;
    new FestivalProgram(root);
});
